import { Check, Clock, Coins, Plus, ShieldCheck, UserRoundCheck, type LucideIcon } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { FirstCardHolderStatus } from "../../hooks/useFirstCardRequirements";
import { FirstCardDiagram } from "./FirstCardDiagram";

type StepStatus = "done" | "in_review" | "pending" | "after" | "unknown";

const STATUS_BADGE = {
  // No icon on "done": the step's own tile already turns into a check.
  done: { variant: "success", icon: undefined, label: "first_card_status_done" },
  in_review: { variant: "warning", icon: Clock, label: "first_card_status_in_review" },
  pending: { variant: "default", icon: undefined, label: "first_card_status_pending" },
  after: { variant: "secondary", icon: undefined, label: "first_card_status_after" },
} as const satisfies Record<Exclude<StepStatus, "unknown">, unknown>;

const HOLDER_STEP_STATUS: Record<FirstCardHolderStatus, StepStatus> = {
  ready: "done",
  in_review: "in_review",
  missing: "pending",
  unknown: "unknown",
};

interface Step {
  icon: LucideIcon;
  title: "first_card_step_security_title" | "first_card_step_holder_title" | "first_card_step_balance_title";
  body: "first_card_step_security_body" | "first_card_step_holder_body" | "first_card_step_balance_body";
  status: StepStatus;
}

export interface FirstCardEmptyStateProps {
  hasSecurityMethod: boolean;
  holderStatus: FirstCardHolderStatus;
  canCreateCard: boolean;
  onCreateCard: () => void;
  onViewHolders: () => void;
}

/**
 * The cards page of an organization that has not issued a card yet: a diagram of how the card comes together,
 * and what issuing it takes, each requirement with where the organization stands on it. One way forward — the
 * create-card flow already asks for the security method and creates the holder on the way — plus the holders
 * list while a holder is in verification, since waiting on one is the only thing that flow cannot fix.
 */
export function FirstCardEmptyState({
  hasSecurityMethod,
  holderStatus,
  canCreateCard,
  onCreateCard,
  onViewHolders,
}: FirstCardEmptyStateProps) {
  const { t } = useTranslations("cards");

  const steps: Step[] = [
    {
      icon: ShieldCheck,
      title: "first_card_step_security_title",
      body: "first_card_step_security_body",
      status: hasSecurityMethod ? "done" : "pending",
    },
    {
      icon: UserRoundCheck,
      title: "first_card_step_holder_title",
      body: "first_card_step_holder_body",
      status: HOLDER_STEP_STATUS[holderStatus],
    },
    {
      icon: Coins,
      title: "first_card_step_balance_title",
      body: "first_card_step_balance_body",
      status: "after",
    },
  ];

  const createButton = (
    <ColocaButton variant="primary" icon={Plus} onClick={onCreateCard} disabled={!canCreateCard}>
      {t("first_card_cta")}
    </ColocaButton>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex items-center justify-center border-b border-border bg-muted/40 px-6 py-10 md:border-r md:border-b-0 md:px-10">
          <FirstCardDiagram className="max-w-[420px]" />
        </div>

        <ColocaReveal className="flex flex-col gap-6 p-6 md:p-8" delay={0.4}>
          <div data-reveal className="space-y-1.5">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("first_card_eyebrow")}
            </p>
            <h2 className="text-xl font-semibold leading-tight text-foreground">{t("first_card_title")}</h2>
            <p className="text-sm text-muted-foreground">{t("first_card_body")}</p>
          </div>

          <ul className="flex flex-col gap-4">
            {steps.map(({ icon: Icon, title, body, status }) => {
              const badge = status === "unknown" ? null : STATUS_BADGE[status];
              const done = status === "done";
              return (
                <li key={title} data-reveal className="flex items-start gap-3">
                  <span
                    className={cn(
                      "mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors",
                      done ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                    )}
                  >
                    {done ? (
                      <Check aria-hidden="true" className="size-4" />
                    ) : (
                      <Icon aria-hidden="true" className="size-4" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="text-sm font-medium text-foreground">{t(title)}</p>
                      {badge && (
                        <ColocaBadge variant={badge.variant} icon={badge.icon}>
                          {t(badge.label)}
                        </ColocaBadge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{t(body)}</p>
                  </div>
                </li>
              );
            })}
          </ul>

          <div data-reveal className="flex flex-wrap items-center gap-2">
            {canCreateCard ? (
              createButton
            ) : (
              <ColocaTooltip trigger={createButton}>{t("no_permission_contact_admin")}</ColocaTooltip>
            )}
            {holderStatus === "in_review" && (
              <ColocaButton variant="ghost" onClick={onViewHolders}>
                {t("first_card_holders_link")}
              </ColocaButton>
            )}
          </div>
        </ColocaReveal>
      </div>
    </section>
  );
}
